import { Injectable, Output, EventEmitter } from '@angular/core';
import { map, switchMap } from 'rxjs/operators';
import { ConfirmDialogRequest, ConfirmDialogComponent, SHIPMENT_CONFIRM_TOKEN } from '../../../site-shipping-tool/shared/confirm-dialog/confirm-dialog.component';
import { DeleteDialogComponent } from '../../../site-shipping-tool/shared/delete-warning/delete-warning-dialog.component';
import { RegisterSampleStorage } from '../register-sample-storage';
import { IMdlCustomDialogConfiguration, MdlDialogService } from '@angular-mdl/core';
import { AccessionData, VisitInfo, ReturnContainerInfo, ErrorMsg } from '../register-sample-info';
import * as moment from 'moment';
import { of, Observable } from 'rxjs';
import { GridHeader, ActionType } from '../../../sample-tracking/tracking-overview/interfaces/grid';
import { AdditionalContainerInfo, AdditionalContainerComponent, ADDITIONAL_CONTAINER_TOKEN } from '../additional-container/additional-container.component';

@Injectable()
export class AccessionDetailsService {

    public alternateSampleList = {
        message: `<b> Alternate Container </b>
                    <p> Please Enter the reason for alternate container. </p>`,
        reverseButtons: true,
        helpText: ` =  Required`,
        dropdownHeader: `Reason `,
        textareaHeader: `Sample Label Description `,
        yesButtonText: 'Next',
        noButtonText: 'Cancel',
        title: 'Select Alternate Container',
        showReasonAreaByDefault: true,
        isAlternateContainer: true,
        textareaPlaceholder: 'Enter sample label description',
        dropDownData: {
            placeholder: 'Please enter reason',
            listItem: [{}]
        },
        textMaxLength: 100,
        displayRequired: true,
    };

    constructor(
        private dialogService: MdlDialogService,
        private registerSampleStorage: RegisterSampleStorage) {
         
    }

    public  deleteSampleRegistrationDialog(accessionNo: string) {
        return this.showDeleteWarning(
            {
                message: `Sample registration has been submitted for this accession: <br> <br> 
                <b>${accessionNo} </b><br> <br>Do you want to delete existing registration and start over?`,
                reverseButtons: true,
                yesButtonText: 'Delete Registration',
                noButtonText: 'Cancel',
                title: 'Samples'
            },
            true);
    }

    public showWarning(message = '', title = '', cssStyle = '') {
        let dialogRequest: ConfirmDialogRequest = {
            message: message.length ? message : `The value entered is not recognized, if you are using a non-LabCorp kit and shipping samples to LabCorp,
                      please proceed to <span class="highlight">Site Shipping Tool</span> to create a shipment.`,
            title: title.length ? title : 'Not a LabCorp Kit',
            isSMAlert: true,
            isWarning: true,
            hideCloseIcon: true,
            warningBtnText: 'Close',
            isKitUpdateWarning: title === ErrorMsg.KitUpdated ? true : false
        };
        return this.showConfirmDialog(dialogRequest, false, cssStyle)
            .pipe(switchMap(dialog => dialog.onHide()), map(dialogResult => dialogResult));
    }

    public validateAdditionalBarcode(containers: ReturnContainerInfo[], barcode: string) {
        let error = '';
        let formatedBarcode = barcode.replace('-', '');
        containers.forEach(ct => {
            let format1 = ct.accessionNumber + ct.containerNumber;
            let format2 = ct.accessionNumber + '0' + ct.containerNumber;
            let formatedCode = ct.barcode ? ct.barcode.replace('-', '') : ct.barcode;
            if (formatedBarcode === format1 || formatedBarcode === format2 || formatedBarcode === ct.barcode
                || barcode === ct.barcode || barcode === formatedCode) {
                error = ErrorMsg.AdditionalContainerError;
            }
        });
        return error;
    }

    public showAdditionalContainerDialog(barcode: string, error = '', isValid = true) {
        let requestInfo: AdditionalContainerInfo = {
            barcode: barcode,
            isMatchingAccession: isValid,
            errorText: error,
        };
        return this.showAdditionalDialog(requestInfo)
            .pipe(switchMap(dialog => dialog.onHide()), map(dialogResult => dialogResult));
    }

    public validatePatientId(value: string, isPatientError = true) {
        let error = '';
        if (value && value.length) {
            let matchSpecialCharacters: RegExpMatchArray = value.match(new RegExp(`[^0-9a-zA-Z-*]`));
            if (matchSpecialCharacters && !isPatientError) {
                return ErrorMsg.InValidBarcode;
            }
            let matchArray: RegExpMatchArray = value.match(new RegExp(`^[0-9a-zA-Z].*`));
            if (!matchArray) {
                return isPatientError ? ErrorMsg.PatientError : ErrorMsg.InValidBarcode;
            }
            let numberMatch: RegExpMatchArray = value.match(new RegExp(`[0-9]+`));
            if (!numberMatch) {
                return isPatientError ? ErrorMsg.PatientError : ErrorMsg.InValidBarcode;
            }
        }
        return error;
    }

    public getSaveRequestData (accessionData: AccessionData, visitInfo: VisitInfo) {
        return {
            accessionId: accessionData.accessionId,
            accessionNumber: accessionData.accessionNumber,
            subProtocolId: accessionData.subProtocolId,
            projectNumber: accessionData.projectNumber,
            investigatorSiteId: accessionData.investigatorSiteId,
            investigatorNumber: accessionData.investigatorNumber,
            investigatorName: accessionData.investigatorName,
            kitRequestedOn: accessionData.kitRequestedOn,
            kitExpiresOn: accessionData.kitExpiresOn,
            kitTypeId: accessionData.kitTypeId,
            kitType: accessionData.kitType,
            kitDescription: accessionData.kitDescription,
            kitboxLabel1: accessionData.kitboxLabel1,
            kitboxLabel2: accessionData.kitboxLabel2,
            visitScheduleId: visitInfo ? visitInfo.visitScheduleId : '',
            protocolVisitCode: visitInfo ? visitInfo.protocolVisitCode : '',
            visitDescription: visitInfo ? visitInfo.protocolVisitDescription : '',
            patientIdentifier: accessionData.patientIdentifier,
            visitCollectionDateTime: accessionData.visitCollectionDateTime + ' ' + accessionData.visitCollectionTime,
            createdOn: `${moment().local().format('YYYY-MM-DD hh:mm:ss Z')}`
        };
    }

    public checkCollectionDateTime(value: string) {
        if (!value || value.length === 0) { return true; }
        const date = moment(value);
        let currentDate = moment().format('YYYY-MM-DD');
        return date && date.isBefore(currentDate) && !date.isSame(currentDate);
    }

    public showDeleteWarning(data: ConfirmDialogRequest, showDeleteDialog = false, cssStyle = '') {
        let dialogRequest: ConfirmDialogRequest = { ...data };
        return this.showConfirmDialog(dialogRequest, showDeleteDialog, cssStyle)
            .pipe(switchMap(dialog => dialog.onHide()), map(dialogResult => dialogResult));
    }

    public deleteRegistrationReasonDialog(accessionNo: string, isYesButtonClicked: boolean): Observable<any> {
        if (isYesButtonClicked) {
            let dialogRequest: ConfirmDialogRequest = {
                message: `Please enter a reason for the deletion of this sample registration: <b>${accessionNo}</b>`,
                reverseButtons: true,
                yesButtonText: 'Delete Registration',
                noButtonText: 'Cancel',
                title: 'Delete Sample Registration Reason',
                dropDownData: {
                    placeholder: 'Enter Reason',
                }
            };
            return this.showConfirmDialog(dialogRequest).pipe(
                switchMap(dialog => dialog.onHide()),
                map(dialogResult => dialogResult));

        }
        return of(false);
    }

    public getContainersHeader(): GridHeader[] {
        return [
            {
                key: 'returnRequirement',
                displayText: 'Required?',
                cssClasses: [
                    {
                        key: 'Required',
                        class: 'required'
                    },
                    {
                        key: 'Optional',
                        class: 'optional'
                    },
                    {
                        key: 'Alt. Container',
                        class: 'alternateContainer'
                    },
                    {
                        key: 'Additional',
                        class: 'optional'
                    }
                ]
            },
            {
                key: 'containerIdentifier',
                displayText: 'Sample',
                sort: 'asc',
                subFields: [{ key: 'label1' }, { key: 'label2' }]
            },
            { 
                key: 'collectionStatus',
                displayText: 'Container Status',
                isDropdownField: true,
                dataStyleClass: 'returnRequirement',
                headerStyleClass: 'align-text',
                childHeader: {
                    defaultValue: ' '
                },
                defaultValue: 'Use Alt. Container',
                disableField: 'disableLink',
                isLink: true
            },
            {
                key: 'returnCondition',
                displayText: 'Expected Return Condition',
                defaultValue: ' ',
            },
            {
                key: '',
                defaultValue: 'Lab Manual Instructions',
                displayText: 'Instructions',
                dataStyleClass: 'returnRequirement',
                childHeader: {
                    isLink: true,
                    defaultValue: 'Remove',
                    icon: 'delete',
                },
                isLink: true,
                imgUrl: '../../../../images/sample_tracking/new-tab.svg' 
            }
        ];
    }

    private showConfirmDialog(dialogRequest: ConfirmDialogRequest, showDeleteDialog = false, cssStyle = '') {

        const configDialog: IMdlCustomDialogConfiguration = {
            component: showDeleteDialog ? DeleteDialogComponent : ConfirmDialogComponent,
            providers: [{
                provide: SHIPMENT_CONFIRM_TOKEN, useValue: dialogRequest
            }],
            isModal: true,
            classes: cssStyle ? cssStyle :  showDeleteDialog ? 'st-report-delete-dialog' : 'st-report-confirmation-dialog',
            styles: { 'border-radius': '30px' },
            clickOutsideToClose: false 
        };

        return this.dialogService.showCustomDialog(configDialog);
    }

    private showAdditionalDialog(dialogRequest: AdditionalContainerInfo) {

        const configDialog: IMdlCustomDialogConfiguration = {
            component: AdditionalContainerComponent,
            providers: [{
                provide: ADDITIONAL_CONTAINER_TOKEN, useValue: dialogRequest
            }],
            isModal: true,
            classes: 'st-report-delete-dialog',
            styles: { 'width': '23%'},
            clickOutsideToClose: false
        };

        return this.dialogService.showCustomDialog(configDialog);
    }
}